import { useTier } from './tiers'

/**
 * Tier-aware durations for framer-motion (Motion reads JS numbers, not
 * CSS vars — these mirror the --dur-* token sets in tokens-v2.css).
 * Seconds, per Motion convention.
 */
export interface TierDurations {
  instant: number
  fast: number
  base: number
  md: number
  slow: number
  page: number
}

const DEFAULT_DUR: TierDurations = { instant: 0.09, fast: 0.16, base: 0.24, md: 0.34, slow: 0.52, page: 0.56 }
const WORK_DUR: TierDurations = { instant: 0, fast: 0.06, base: 0.08, md: 0.1, slow: 0.12, page: 0.12 }

export function useTierDurations(): TierDurations {
  const { isWork } = useTier()
  return isWork ? WORK_DUR : DEFAULT_DUR
}

export const EASE_DECEL: [number, number, number, number] = [0.05, 0.7, 0.1, 1]
export const EASE_STANDARD: [number, number, number, number] = [0.22, 1, 0.36, 1]

/**
 * Standardized slide/morph variants (U8 promotion). A reusable Motion
 * variant set for list/section slide-ins — the hard-cut reveals that the
 * CSS `fx-rise` staggers don't already cover. Tier-scaled: durations come
 * from `useTierDurations()` so Work Mode collapses them to near-instant,
 * and the offset zeroes out in Work so the engines truly idle (no lingering
 * transform), mirroring the gated-CSS recipes.
 *
 * Usage (component):
 *   const dur = useTierDurations()
 *   const { isWork } = useTier()
 *   <motion.div variants={slideVariants(dur, isWork)}
 *               initial="hidden" animate="shown" />
 * or via the index-staggered transition:
 *   transition={slideTransition(dur, isWork, i)}
 */
export interface MotionVariant {
  opacity: number
  y: number
  transition?: { duration: number; ease: [number, number, number, number] }
}

/** Default slide offset (px) the reveal travels in; 0 in Work Mode. */
export const SLIDE_OFFSET = 14

export function slideOffset(isWork: boolean): number {
  return isWork ? 0 : SLIDE_OFFSET
}

/**
 * Variant set for `<motion.* variants={…} initial="hidden" animate="shown">`.
 * `hidden` carries the offset; `shown` settles with a tier-scaled, decel
 * ease. Pure — unit-tested.
 */
export function slideVariants(
  dur: TierDurations,
  isWork: boolean,
): { hidden: MotionVariant; shown: MotionVariant } {
  return {
    hidden: { opacity: 0, y: slideOffset(isWork) },
    shown: { opacity: 1, y: 0, transition: { duration: dur.md, ease: EASE_DECEL } },
  }
}

/**
 * A single index-staggered slide transition (for lists that map a flat
 * array). Stagger is suppressed in Work Mode and clamped so long lists
 * never accumulate a visible cascade. Pure — unit-tested.
 */
export function slideTransition(
  dur: TierDurations,
  isWork: boolean,
  index = 0,
): { duration: number; ease: [number, number, number, number]; delay: number } {
  const step = 0.04
  const maxDelay = 0.29
  return {
    duration: dur.md,
    ease: EASE_DECEL,
    delay: isWork ? 0 : Math.min(index * step, maxDelay),
  }
}
